import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    sendPasswordResetEmail as firebaseSendPasswordResetEmail,
    updatePassword,
    onAuthStateChanged
} from 'firebase/auth';
import {
    doc,
    getDoc,
    setDoc,
    updateDoc,
    Timestamp
} from 'firebase/firestore';
import { auth, db } from '../../config/firebase';

const USERS_COLLECTION = 'users';

/**
 * Login with Firebase — delegates all profile/role resolution to fetchUserData
 */
export const login = async (email, password) => {
    try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // Fetch complete user profile with role resolution
        const userData = await fetchUserData(user);

        const idToken = await user.getIdToken();
        localStorage.setItem('firebaseToken', idToken);

        return {
            success: true,
            message: 'Login successful',
            data: {
                user: userData,
                token: idToken
            }
        };
    } catch (error) {
        console.error('Login error:', error);

        const errorMessages = {
            'auth/user-not-found': 'No user found with this email',
            'auth/wrong-password': 'Incorrect password',
            'auth/invalid-email': 'Invalid email address',
            'auth/user-disabled': 'This account has been disabled',
            'auth/too-many-requests': 'Too many attempts. Please try again later',
            'auth/network-request-failed': 'Network error. Please check your connection',
            'auth/invalid-credential': 'Invalid email or password'
        };

        const message = errorMessages[error.code] || error.message;
        throw new Error(message);
    }
};

/**
 * Register new user
 */
export const register = async (userData) => {
    try {
        const { email, password, ...profileData } = userData;

        // Create Firebase auth user
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // Create user profile in Firestore
        const userProfile = {
            email: user.email,
            firstName: profileData.firstName || '',
            lastName: profileData.lastName || '',
            phone: profileData.phone || '',
            department: profileData.department || '',
            designation: profileData.designation || '',
            role: profileData.role || 'employee',
            isActive: true,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
        };

        await setDoc(doc(db, USERS_COLLECTION, user.uid), userProfile);

        // Save to localStorage
        const fullUserData = {
            id: user.uid,
            ...userProfile
        };
        localStorage.setItem('user', JSON.stringify(fullUserData));
        localStorage.setItem('firebaseToken', await user.getIdToken());

        return {
            success: true,
            message: 'Registration successful',
            data: {
                user: fullUserData,
                token: await user.getIdToken()
            }
        };
    } catch (error) {
        console.error('Registration error:', error);

        const errorMessages = {
            'auth/email-already-in-use': 'Email already registered',
            'auth/weak-password': 'Password should be at least 6 characters',
            'auth/invalid-email': 'Invalid email address',
            'auth/operation-not-allowed': 'Email/password accounts are not enabled'
        };

        const message = errorMessages[error.code] || error.message;
        throw new Error(message);
    }
};

/**
 * Logout
 */
export const logout = async () => {
    try {
        await signOut(auth);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('firebaseToken');

        return {
            success: true,
            message: 'Logout successful'
        };
    } catch (error) {
        console.error('Logout error:', error);
        throw new Error(error.message || 'Logout failed');
    }
};

/**
 * Send password reset email
 */
export const forgotPassword = async (email) => {
    try {
        await firebaseSendPasswordResetEmail(auth, email);

        return {
            success: true,
            message: 'Password reset email sent successfully'
        };
    } catch (error) {
        console.error('Password reset error:', error);

        const errorMessages = {
            'auth/user-not-found': 'No user found with this email',
            'auth/invalid-email': 'Invalid email address',
            'auth/too-many-requests': 'Too many requests. Please try again later'
        };

        const message = errorMessages[error.code] || error.message;
        throw new Error(message);
    }
};

/**
 * Reset password (for logged-in user)
 */
export const resetPassword = async (newPassword) => {
    try {
        const user = auth.currentUser;
        if (!user) {
            throw new Error('No user logged in');
        }

        await updatePassword(user, newPassword);

        return {
            success: true,
            message: 'Password updated successfully'
        };
    } catch (error) {
        console.error('Password update error:', error);
        throw new Error(error.message || 'Failed to update password');
    }
};

/**
 * Admin/HR: Send password reset link to employee email
 * Marks employee Firestore record so Admin/HR can see reset is pending
 */
export const sendEmployeePasswordResetEmail = async (employeeEmail, employeeDocId) => {
    try {
        // Send Firebase reset email
        await firebaseSendPasswordResetEmail(auth, employeeEmail);

        // Mark the employee Firestore record so Admin/HR knows reset link was sent
        if (employeeDocId) {
            const { doc, updateDoc, Timestamp: FsTimestamp } = await import('firebase/firestore');
            const empRef = doc(db, 'employees', employeeDocId);
            await updateDoc(empRef, {
                loginPassword: '__RESET_LINK_SENT__',
                passwordStatus: 'reset_link_sent',
                passwordResetSentAt: FsTimestamp.now()
            });
        }

        return {
            success: true,
            message: `Password reset email sent to ${employeeEmail}`
        };
    } catch (error) {
        console.error('Employee password reset error:', error);
        const errorMessages = {
            'auth/user-not-found': 'No Firebase account found for this employee',
            'auth/invalid-email': 'Invalid employee email address',
            'auth/too-many-requests': 'Too many requests. Please try again later'
        };
        const message = errorMessages[error.code] || error.message;
        throw new Error(message);
    }
};

/**
 * Admin/HR: Directly update an employee's stored password in Firestore
 * This sets both the loginPassword and resets the passwordStatus to 'admin_set'
 */
export const updateEmployeeStoredPassword = async (employeeDocId, newPassword) => {
    try {
        const { doc, getDoc, updateDoc, Timestamp: FsTimestamp } = await import('firebase/firestore');
        const empRef = doc(db, 'employees', employeeDocId);

        // Fetch employee to get email
        const empSnap = await getDoc(empRef);
        if (!empSnap.exists()) {
            throw new Error('Employee not found');
        }

        const empData = empSnap.data();
        const email = empData.personal?.email || empData.personalInfo?.email || empData.email;

        if (!email) {
            throw new Error('Employee email not found to update auth password');
        }

        await updateDoc(empRef, {
            loginPassword: newPassword,
            passwordStatus: 'admin_set',
            passwordChangedAt: FsTimestamp.now()
        });
        return { success: true };
    } catch (error) {
        console.error('Update stored password error:', error);
        throw new Error(error.message || 'Failed to update password record');
    }
};


/**
 * Get current user
 */
export const getCurrentUser = async () => {
    try {
        const user = auth.currentUser;

        if (!user) {
            throw new Error('No user logged in');
        }

        const userDoc = await getDoc(doc(db, USERS_COLLECTION, user.uid));

        if (!userDoc.exists()) {
            throw new Error('User profile not found');
        }

        return {
            success: true,
            data: {
                id: user.uid,
                email: user.email,
                ...userDoc.data()
            }
        };
    } catch (error) {
        console.error('Get current user error:', error);
        throw new Error(error.message || 'Failed to get current user');
    }
};

/**
 * Update user profile
 */
export const updateProfile = async (userId, profileData) => {
    try {
        const userRef = doc(db, USERS_COLLECTION, userId);

        const updateData = {
            ...profileData,
            updatedAt: Timestamp.now()
        };

        await updateDoc(userRef, updateData);

        // Update localStorage
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        const updatedUser = { ...currentUser, ...updateData };
        localStorage.setItem('user', JSON.stringify(updatedUser));

        return {
            success: true,
            message: 'Profile updated successfully',
            data: updatedUser
        };
    } catch (error) {
        console.error('Update profile error:', error);
        throw new Error(error.message || 'Failed to update profile');
    }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
    return !!auth.currentUser || !!localStorage.getItem('firebaseToken');
};

/**
 * Get user from localStorage
 */
export const getUser = () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
};

/**
 * Get Firebase ID token
 */
export const getFirebaseIdToken = async () => {
    const user = auth.currentUser;
    if (user) {
        return await user.getIdToken();
    }
    return null;
};

/**
 * Auth state observer
 */
export const fetchUserData = async (firebaseUser) => {
    try {
        const userDoc = await getDoc(doc(db, USERS_COLLECTION, firebaseUser.uid));
        let userData = {
            id: firebaseUser.uid,
            uid: firebaseUser.uid,
            email: firebaseUser.email
        };

        if (userDoc.exists()) {
            userData = {
                ...userData,
                ...userDoc.data()
            };
        }

        // Try to find linked employee record — UID first, email as fallback
        try {
            const { collection: firestoreCollection, getDocs: firestoreGetDocs } = await import('firebase/firestore');
            const employeesRef = firestoreCollection(db, 'employees');
            const employeeSnapshot = await firestoreGetDocs(employeesRef);

            let foundEmployee = null;

            // Priority 1: Match by Firebase UID
            for (const docSnap of employeeSnapshot.docs) {
                const data = docSnap.data();
                if (data.uid === firebaseUser.uid || data.firebaseUid === firebaseUser.uid) {
                    foundEmployee = { id: docSnap.id, data };
                    break;
                }
            }

            // Priority 2: Match by email
            if (!foundEmployee) {
                employeeSnapshot.forEach((docSnap) => {
                    if (foundEmployee) return;
                    const data = docSnap.data();
                    const emails = [
                        data.personal?.email,
                        data.personalInfo?.email,
                        data.email
                    ].filter(Boolean).map(e => e.toLowerCase());

                    if (emails.includes(firebaseUser.email.toLowerCase())) {
                        foundEmployee = { id: docSnap.id, data };
                    }
                });
            }

            // If found by personal email, store UID for future fast/accurate lookups
            if (foundEmployee && !foundEmployee.data.uid && !foundEmployee.data.firebaseUid) {
                try {
                    const { doc: fsDoc2, updateDoc: fsUpdate2 } = await import('firebase/firestore');
                    const empRef2 = fsDoc2(db, 'employees', foundEmployee.id);
                    await fsUpdate2(empRef2, { uid: firebaseUser.uid });
                    console.log('[AuthService] Stored UID in employee record for accurate lookups');
                } catch (uidErr) {
                    console.warn('[AuthService] Could not store UID:', uidErr);
                }
            }

            if (foundEmployee) {
                const empData = foundEmployee.data;
                userData.employeeId = foundEmployee.id;
                userData.employeeCode = empData.job?.employeeCode || empData.jobDetails?.employeeCode || foundEmployee.id;
                userData.employeeName = empData.personal?.fullName ||
                    `${empData.personal?.firstName || ''} ${empData.personal?.lastName || ''}`.trim() ||
                    empData.personalInfo?.fullName ||
                    `${empData.personalInfo?.firstName || ''} ${empData.personalInfo?.lastName || ''}`.trim() ||
                    empData.firstName || 'Unknown';

                userData.designation = empData.job?.designation || empData.jobDetails?.position || empData.jobDetails?.designation || empData.designation || '';
                userData.department = empData.job?.department || empData.jobDetails?.department || empData.department || '';
                userData.joiningDate = empData.job?.joiningDate || empData.jobDetails?.joinDate || empData.jobDetails?.joiningDate || empData.joiningDate || '';
                userData.employmentType = empData.job?.employmentType || empData.jobDetails?.employmentType || empData.employmentType || '';

                userData.firstName = empData.personal?.firstName || empData.personalInfo?.firstName || empData.firstName || '';
                userData.lastName = empData.personal?.lastName || empData.personalInfo?.lastName || empData.lastName || '';
                userData.reportingManager = empData.job?.reportingManager || empData.jobDetails?.reportingTo || empData.reportingManager || '';
                userData.reportingManagerName = empData.job?.reportingManagerName || empData.jobDetails?.reportingManagerName || empData.reportingManagerName || '';
                userData.job = empData.job || {};

                // Extract profile photo from all possible employee schema variations
                const empPhoto = empData.personal?.photo || 
                                 empData.photoURL || 
                                 empData.photo || 
                                 empData.personalInfo?.photo || 
                                 empData.profilePicture || 
                                 userDoc.data()?.photoURL || 
                                 userDoc.data()?.photo || 
                                 firebaseUser.photoURL || 
                                 '';
                userData.photo = empPhoto;
                userData.photoURL = empPhoto;
                if (!userData.personal) {
                    userData.personal = {};
                }
                userData.personal.photo = empPhoto;

                // Get role from employee data
                let empRole = empData.job?.role || empData.role;

                // Fallback / Auto-mapping based on designation for safety
                const designationLower = userData.designation?.toLowerCase();
                if (designationLower) {
                    if (designationLower.includes('production head')) {
                        empRole = 'production_head';
                    } else if (designationLower.includes('department head') || designationLower.includes('dept head')) {
                        empRole = 'dept_head';
                    } else if (designationLower.includes('director')) {
                        empRole = 'director';
                    }
                }

                if (empRole) {
                    const normalizedRole = empRole.toLowerCase();
                    if (userData.role !== normalizedRole) {
                        userData.role = normalizedRole;
                        try {
                            const userRef = doc(db, USERS_COLLECTION, firebaseUser.uid);
                            await updateDoc(userRef, { role: normalizedRole });
                            console.log(`[AuthService] Synchronized role "${normalizedRole}" to user doc`);
                        } catch (syncError) {
                            console.warn('[AuthService] Failed to sync role to user doc:', syncError);
                        }
                    }
                }
            }
        } catch (e) {
            console.warn('[AuthService] Could not fetch linked employee details:', e);
        }

        // Fallback for photo if not found from employee doc
        if (!userData.photoURL && !userData.photo) {
            const fallbackPhoto = userDoc.data()?.photoURL || userDoc.data()?.photo || firebaseUser.photoURL || '';
            userData.photo = fallbackPhoto;
            userData.photoURL = fallbackPhoto;
            if (!userData.personal) {
                userData.personal = {};
            }
            userData.personal.photo = fallbackPhoto;
        }

        if (!userData.role) {
            userData.role = 'employee';
        } else {
            userData.role = userData.role.toLowerCase();
        }

        // Save to localStorage
        localStorage.setItem('user', JSON.stringify(userData));
        return userData;
    } catch (error) {
        console.error('[AuthService] fetchUserData error:', error);
        return null;
    }
};

export const onAuthStateChange = (callback) => {
    return onAuthStateChanged(auth, callback);
};

const authService = {
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    getCurrentUser,
    updateProfile,
    isAuthenticated,
    getUser,
    getFirebaseIdToken,
    onAuthStateChange,
    fetchUserData
};

export default authService;
